<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { ChevronLeft, ChevronRight, Newspaper, Calendar, User, Clock } from 'lucide-vue-next'
import sectorBg from '../assets/img/aaa.jpg'
import userImg from '@/assets/img/user.png'
import api from '@/services/api'

interface NewsItem {
  id: number
  title: string
  content: string
  slug: string
  image: string
  published_date: string
}

const newsItems = ref<NewsItem[]>([])
const currentIndex = ref(0)
const intervalId = ref<number | null>(null)
const isHovered = ref(false)

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

const truncateContent = (content: string, length: number) => {
  const stripped = content.replace(/<[^>]*>/g, '')
  return stripped.length > length ? stripped.substring(0, length) + '...' : stripped
}

const currentNewsItem = computed(() => newsItems.value[currentIndex.value])

const nextNews = () => {
  currentIndex.value = (currentIndex.value + 1) % newsItems.value.length
}

const prevNews = () => {
  currentIndex.value = (currentIndex.value - 1 + newsItems.value.length) % newsItems.value.length
}

const pauseSlideshow = () => {
  if (intervalId.value) {
    clearInterval(intervalId.value)
    intervalId.value = null
  }
}

const resumeSlideshow = () => {
  if (!intervalId.value) {
    intervalId.value = setInterval(nextNews, 5000) as unknown as number
  }
}

const fetchNewsAndEvents = async () => {
  try {
    const response = await api.get('/webcontents/api/news-and-events/')
    newsItems.value = response.data
  } catch (error) {
    console.error('Error fetching news and events:', error)
  }
}

onMounted(() => {
  fetchNewsAndEvents()
  intervalId.value = setInterval(nextNews, 5000) as unknown as number
})

onUnmounted(() => {
  if (intervalId.value) clearInterval(intervalId.value)
})
</script>

<template>
  <section
    class="relative bg-cover bg-center py-20"
    :style="{ backgroundImage: `url(${sectorBg})` }"
  >
    <!-- Overlay with gradient -->
    <div class="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>

    <div class="container mx-auto px-4 relative z-10">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <!-- Left Side: Title and Description -->
        <div class="text-white space-y-6">
          <div class="inline-flex items-center space-x-2 bg-bhutan-green/20 px-4 py-2 rounded-full">
            <Newspaper class="text-bhutan-green" size="20" />
            <span class="text-sm font-medium uppercase tracking-wider">Latest Updates</span>
          </div>
          
          <h2 class="text-5xl font-bold leading-tight">
            News and <span class="text-bhutan-green">Events</span>
          </h2>
          
          <p class="text-gray-300 text-lg max-w-xl">
            Stay updated with the latest news, announcements, and upcoming events from our organization.
          </p>

          <div class="flex items-center space-x-4 pt-4">
            <router-link
              to="/news"
              class="bg-bhutan-green hover:bg-bhutan-green-light text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg inline-flex items-center space-x-2"
            >
              <span>View All News</span>
              <ChevronRight size="20" />
            </router-link>

            <!-- Navigation Dots -->
            <div class="flex items-center space-x-2">
              <button
                v-for="(_, index) in newsItems"
                :key="index"
                @click="currentIndex = index"
                class="w-2.5 h-2.5 rounded-full transition-all duration-300"
                :class="[
                  currentIndex === index
                    ? 'bg-bhutan-green w-6'
                    : 'bg-white/50 hover:bg-white'
                ]"
              />
            </div>
          </div>
        </div>

        <!-- Right Side: News Card -->
        <div 
          v-if="newsItems.length > 0"
          class="relative"
          @mouseenter="isHovered = true; pauseSlideshow()"
          @mouseleave="isHovered = false; resumeSlideshow()"
        >
          <div class="bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-[1.02] transition-all duration-500">
            <!-- News Content -->
            <div class="p-8">
              <div class="flex items-center justify-between mb-6">
                <div class="flex items-center space-x-4">
                  <img :src="userImg" alt="Admin" class="w-12 h-12 rounded-full ring-2 ring-bhutan-green/20" />
                  <div>
                    <h4 class="font-medium text-gray-900">Admin</h4>
                    <div class="flex items-center text-sm text-gray-500 space-x-4">
                      <span class="flex items-center space-x-1">
                        <Calendar size="14" />
                        <span>{{ formatDate(currentNewsItem.published_date) }}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <Newspaper class="text-bhutan-green" size="32" />
              </div>

              <h3 class="text-2xl font-bold text-gray-900 mb-4 line-clamp-2">
                {{ currentNewsItem.title }}
              </h3>

              <p class="text-gray-600 mb-6 line-clamp-3">
                {{ truncateContent(currentNewsItem.content, 200) }}
              </p>

              <router-link
                :to="`/news-and-events/${currentNewsItem.slug}`"
                class="inline-flex items-center space-x-2 text-bhutan-green font-medium hover:text-bhutan-green-light transition-colors duration-300"
              >
                <span>Read Full Article</span>
                <ChevronRight size="20" />
              </router-link>
            </div>
          </div>

          <!-- Navigation Buttons -->
          <div class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2">
            <button
              @click.prevent="prevNews"
              class="bg-white p-3 rounded-full shadow-lg hover:bg-bhutan-green hover:text-white transition-all duration-300 transform hover:scale-110"
            >
              <ChevronLeft size="24" />
            </button>
          </div>

          <div class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2">
            <button
              @click.prevent="nextNews"
              class="bg-white p-3 rounded-full shadow-lg hover:bg-bhutan-green hover:text-white transition-all duration-300 transform hover:scale-110"
            >
              <ChevronRight size="24" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>